import type { SessionStatus } from '@zeus/index'
import type { ReactNode } from 'react'
import type { FilterGroup, SessionFiltersState } from '~/pages/backoffice/admin/sessions/use-session-filters'
import { useTranslation } from 'react-i18next'
import { tourAnchor } from '~/helpers/tour/anchors'
import { SESSION_STATUS_KEYS } from '~/pages/backoffice/admin/sessions/session-status'
import { FilterSelect, SearchInput, TableToolbar, ToolbarActions } from '~/pages/backoffice/TableToolbar'

type IProps = {
  filters: SessionFiltersState
  /** Groupes proposés ; vide = pas de filtre par groupe (aucun groupe, ou pas le droit de les voir). */
  groups: readonly FilterGroup[]
  /** Les actions de la barre (créer un parcours), selon les droits. */
  actions?: ReactNode
}

/** La barre de la liste des parcours : recherche, filtres combinables (#107, #29, #244), actions. */
function SessionsToolbar({ filters, groups, actions }: IProps) {
  const { t } = useTranslation()

  return (
    <TableToolbar>
      <SearchInput
        {...tourAnchor('sessions.search')}
        value={filters.search}
        onChange={e => filters.setSearch(e.target.value)}
      />
      <FilterSelect
        aria-label={t('common.status')}
        {...tourAnchor('sessions.filters')}
        value={filters.status}
        onChange={e => filters.setStatus(e.target.value as SessionStatus | '')}
      >
        <option value="">{t('common.allStatuses')}</option>
        {Object.entries(SESSION_STATUS_KEYS).map(([value, key]) => (
          <option key={value} value={value}>{t(key)}</option>
        ))}
      </FilterSelect>
      <FilterSelect aria-label={t('common.module')} value={filters.moduleId} onChange={e => filters.setModuleId(e.target.value)}>
        <option value="">{t('sessions.allModules')}</option>
        {filters.moduleOptions.map(module => (
          <option key={module.id} value={module.id}>{module.title}</option>
        ))}
      </FilterSelect>
      {groups.length > 0 && (
        <FilterSelect aria-label={t('common.groups')} value={filters.groupId} onChange={e => filters.setGroupId(e.target.value)}>
          <option value="">{t('groups.all')}</option>
          {groups.map(group => (
            <option key={group.id} value={group.id}>{group.name}</option>
          ))}
        </FilterSelect>
      )}
      {filters.tagOptions.length > 0 && (
        <FilterSelect aria-label={t('sessions.tags.label')} value={filters.tagId} onChange={e => filters.setTagId(e.target.value)}>
          <option value="">{t('sessions.tags.all')}</option>
          {filters.tagOptions.map(tag => (
            <option key={tag.id} value={tag.id}>{tag.name}</option>
          ))}
        </FilterSelect>
      )}
      {filters.trainerOptions.length > 0 && (
        <FilterSelect aria-label={t('sessions.trainers.label')} value={filters.trainerId} onChange={e => filters.setTrainerId(e.target.value)}>
          <option value="">{t('sessions.trainers.all')}</option>
          {filters.trainerOptions.map(trainer => (
            <option key={trainer.id} value={trainer.id}>{trainer.name}</option>
          ))}
        </FilterSelect>
      )}
      {actions && <ToolbarActions>{actions}</ToolbarActions>}
    </TableToolbar>
  )
}

export default SessionsToolbar
